import { COLOR_LIST, ColorTrait } from "@/data/colors";

/** Shared low-level color math used by the color scanner, aura scanner, and
 *  color synergy pages — kept in one place so hue/RGB conversions stay
 *  consistent across features. */

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16) || 0,
    g: parseInt(h.slice(2, 4), 16) || 0,
    b: parseInt(h.slice(4, 6), 16) || 0,
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.min(255, Math.max(0, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function colorDistance(hexA: string, hexB: string): number {
  const a = hexToRgb(hexA), b = hexToRgb(hexB);
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

/** Perceptual 50/50 blend of two hex colors (simple linear RGB average —
 *  good enough for a visual "synergy" swatch, not color-managed mixing). */
export function mixHex(hexA: string, hexB: string): string {
  const a = hexToRgb(hexA), b = hexToRgb(hexB);
  return rgbToHex((a.r + b.r) / 2, (a.g + b.g) / 2, (a.b + b.b) / 2);
}

/** Find the closest color in the 175-color psychology library to an
 *  arbitrary hex value, so free-form input (uploaded photos, hand-picked
 *  hex codes) can still surface a meaning/keyword write-up. */
export function findNearestLibraryColor(hex: string): { color: ColorTrait; distance: number } {
  let best = COLOR_LIST[0];
  let bestDist = Infinity;
  for (const c of COLOR_LIST) {
    const d = colorDistance(hex, c.hex);
    if (d < bestDist) { bestDist = d; best = c; }
  }
  return { color: best, distance: Math.round(bestDist) };
}

export function isValidHex(value: string): boolean {
  return /^#?[0-9a-fA-F]{6}$/.test(value.trim());
}

export function normalizeHex(value: string): string {
  const v = value.trim().replace(/^#/, "").toUpperCase();
  return `#${v}`;
}
